<script setup lang="ts">
import {
  BellOutlined,
  BgColorsOutlined,
  LoginOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons-vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import avatarDefaultUrl from '@/assets/avatar-default.svg?url'
import { getDisplayUrl } from '@/services/storage'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'

const themeStore = useThemeStore()
const auth = useAuthStore()
const router = useRouter()
const route = useRoute()

const showNotificationBadge = ref(false)
const notifCount = ref(0)
const notifTab = ref('notifications')

onMounted(() => {
  if ('Notification' in window) {
    showNotificationBadge.value = Notification.permission === 'default'
  }
})

const avatarError = ref(false)
const avatarSrc = ref(avatarDefaultUrl)

watch(
  () => auth.user?.avatar,
  async (val) => {
    if (avatarError.value || !val) {
      avatarSrc.value = avatarDefaultUrl
      return
    }
    avatarSrc.value = await getDisplayUrl(val)
  },
  { immediate: true },
)

function handleAvatarError() {
  avatarError.value = true
  avatarSrc.value = avatarDefaultUrl
}

const authMenuItems = computed(() => {
  if (auth.token) {
    return [
      { key: 'profile', label: 'Profile', icon: UserOutlined },
      { key: 'logout', label: 'Logout', icon: LogoutOutlined },
    ]
  }
  return [{ key: 'login', label: 'Login', icon: LoginOutlined }]
})

function handleThemeChange(e: any) {
  themeStore.setTheme(e.target.value)
}

function handleAuthMenu(info: { key?: string } | any) {
  const key = String(info?.key ?? '')
  if (key === 'profile') {
    router.push('/profile')
  } else if (key === 'login') {
    router.push('/login')
  } else if (key === 'logout') {
    auth.logout()
  }
}

defineProps<{ collapsed: boolean; isMobile?: boolean }>()
defineEmits<{ 'update:collapsed': [value: boolean]; openDrawer: [] }>()
</script>

<template>
  <a-layout-header class="navbar">
    <menu-fold-outlined
      v-if="!isMobile && !collapsed"
      class="trigger"
      @click="$emit('update:collapsed', true)"
    />
    <menu-unfold-outlined
      v-else-if="!isMobile && collapsed"
      class="trigger"
      @click="$emit('update:collapsed', false)"
    />
    <menu-outlined v-else class="trigger" @click="$emit('openDrawer')" />
    <span class="title">{{ route.meta.title || 'CasaMana' }}</span>
    <div class="navbar-right">
      <a-dropdown :trigger="['click']" placement="bottomRight">
        <a-badge :count="notifCount" :overflow-count="9" :show-zero="false">
          <bell-outlined class="trigger" />
        </a-badge>
        <template #overlay>
          <div style="width: 320px; background: var(--bg-container); border-radius: 8px; box-shadow: 0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px rgba(0,0,0,0.08); padding: 12px">
            <div style="padding: 24px 12px; text-align: center; color: var(--text-secondary); font-size: 13px">
              No new notifications
            </div>
            <a-divider style="margin: 8px 0" />
            <div style="text-align: center">
              <a-button type="link" size="small" @click="router.push('/settings/notifications')">Notification Settings</a-button>
            </div>
          </div>
        </template>
      </a-dropdown>
      <a-dropdown :trigger="['click']" placement="bottomRight">
        <bg-colors-outlined class="trigger" />
        <template #overlay>
          <a-menu>
            <a-menu-item>
              <a-radio-group
                :value="themeStore.theme"
                @change="handleThemeChange"
                style="display: flex; flex-direction: column; gap: 8px; padding: 4px 0"
              >
                <a-radio value="light">Light</a-radio>
                <a-radio value="dark">Dark</a-radio>
                <a-radio value="system">System</a-radio>
              </a-radio-group>
            </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>
      <a-dropdown :trigger="['click']">
        <a-avatar style="cursor: pointer; background-color: #1677ff">
          <img :src="avatarSrc" @error="handleAvatarError" alt="Avatar" style="width: 100%; height: 100%; object-fit: cover" />
        </a-avatar>
        <template #overlay>
          <a-menu @click="handleAuthMenu">
            <a-menu-item v-for="item in authMenuItems" :key="item.key">
              <component :is="item.icon" />
              {{ item.label }}
            </a-menu-item>
          </a-menu>
        </template>
      </a-dropdown>
    </div>
  </a-layout-header>
</template>

<style scoped>
.navbar {
  background: var(--bg-container);
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}
.trigger {
  font-size: 18px;
  cursor: pointer;
}
.title {
  font-size: 16px;
  font-weight: 500;
}
.navbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
}
</style>
